import { BoldIcon, ItalicIcon, LinkIcon, ListIcon, ListOrderedIcon, RemoveFormattingIcon, UnderlineIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { inputClasses } from "@/components/ui/input";
import { SimpleTooltip } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { isEmail } from "./api";

/** Email addresses as chips: type and press Enter, comma or semicolon (pasting a list works too). */
export function AddressField({
  id,
  value,
  onChange,
  placeholder,
  invalid,
}: {
  id: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  invalid?: boolean;
}) {
  const [text, setText] = useState("");
  const commit = (raw: string) => {
    const parts = raw.split(/[;,\s]+/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return;
    const seen = new Set(value.map((v) => v.toLowerCase()));
    onChange([...value, ...parts.filter((p) => !seen.has(p.toLowerCase()) && (seen.add(p.toLowerCase()), true))]);
    setText("");
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === ";") {
      e.preventDefault();
      commit(text);
    } else if (e.key === "Backspace" && !text && value.length) {
      onChange(value.slice(0, -1));
    }
  };
  return (
    <div
      className={cn(
        inputClasses,
        "flex h-auto min-h-9 flex-wrap items-center gap-1 py-1",
        invalid && "border-destructive",
      )}
      onClick={() => document.getElementById(id)?.focus()}
    >
      {value.map((a) => (
        <span
          key={a}
          className={cn(
            "inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs",
            !isEmail(a) && "bg-destructive/10 text-destructive",
          )}
          title={isEmail(a) ? undefined : "Not an email address"}
        >
          {a}
          <button type="button" aria-label={`Remove ${a}`} className="rounded hover:bg-foreground/10" onClick={() => onChange(value.filter((v) => v !== a))}>
            <XIcon className="size-3" />
          </button>
        </span>
      ))}
      <input
        id={id}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(text)}
        onPaste={(e) => {
          const pasted = e.clipboardData.getData("text");
          if (/[;,\s]/.test(pasted.trim())) {
            e.preventDefault();
            commit(pasted);
          }
        }}
        placeholder={value.length ? undefined : placeholder}
        className="min-w-32 flex-1 bg-transparent text-sm outline-none"
        aria-invalid={invalid || undefined}
      />
    </div>
  );
}

function ToolButton({ label, onAction, children }: { label: string; onAction: () => void; children: ReactNode }) {
  return (
    <SimpleTooltip label={label}>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={label}
        // Keep the selection in the editor.
        onMouseDown={(e) => e.preventDefault()}
        onClick={onAction}
      >
        {children}
      </Button>
    </SimpleTooltip>
  );
}

/**
 * The email's wording, edited as formatted text. `initialHtml` is loaded once per `docKey` (so typing never
 * resets the caret); every edit reports the HTML.
 */
export function RichTextEditor({
  id,
  docKey,
  initialHtml,
  onChange,
  labelledBy,
}: {
  id: string;
  docKey: string;
  initialHtml: string;
  onChange: (html: string) => void;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = initialHtml;
    // Only when a different email is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docKey]);
  const run = (command: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    if (ref.current) onChange(ref.current.innerHTML);
  };
  const link = () => {
    const url = window.prompt("Link address (https://…)");
    if (url && /^(https?:|mailto:)/i.test(url.trim())) run("createLink", url.trim());
  };
  return (
    <div className="rounded-md border border-input bg-card shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/30">
      <div role="toolbar" aria-label="Formatting" className="flex flex-wrap items-center gap-0.5 border-b px-1 py-0.5">
        <ToolButton label="Bold" onAction={() => run("bold")}>
          <BoldIcon />
        </ToolButton>
        <ToolButton label="Italic" onAction={() => run("italic")}>
          <ItalicIcon />
        </ToolButton>
        <ToolButton label="Underline" onAction={() => run("underline")}>
          <UnderlineIcon />
        </ToolButton>
        <ToolButton label="Bulleted list" onAction={() => run("insertUnorderedList")}>
          <ListIcon />
        </ToolButton>
        <ToolButton label="Numbered list" onAction={() => run("insertOrderedList")}>
          <ListOrderedIcon />
        </ToolButton>
        <ToolButton label="Link" onAction={link}>
          <LinkIcon />
        </ToolButton>
        <ToolButton label="Clear formatting" onAction={() => run("removeFormat")}>
          <RemoveFormattingIcon />
        </ToolButton>
      </div>
      <div
        id={id}
        ref={ref}
        role="textbox"
        aria-multiline="true"
        aria-labelledby={labelledBy}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        className="prose-email max-h-[45dvh] min-h-48 overflow-y-auto px-3 py-2 text-sm outline-none [&_a]:text-primary [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-5"
      />
    </div>
  );
}
